import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { NavController, ToastController } from '@ionic/angular';
import { Observable, Subject, tap } from 'rxjs';
import { Authentication } from '../core/authentication';
import { Cache, ContentType, Request, RequestMethod, RequestOptions } from '../core/data-xs';
import { AppDebug } from '../core/debug';
import { ProfielData } from '../models/ProfielData';
import { ResetPassword } from '../models/ResetPassword';
import { User } from '../models/User';
import { GlobalService } from './global.service';
import { MetadataService } from './metadata.service';
import { NotificationService } from './notification.service';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceId, DeviceInfo } from '@capacitor/device';
import { Config } from '../core/config';
import { Lid } from '../models/Lid';
import { Widget } from '../models/Widget';
import { Land } from '../models/Land';
import { Vestiging } from '../models/Vestiging';
import { InitialDataLoad } from '../models/InitialDataLoad';
import { AddressInfo } from '../models/AddressInfo';
import { LesRoosterFilter } from '../models/LesRoosterFilter';
import { LesRooster } from '../models/LesRooster';
import { LessenFilter } from '../models/LessenFilter';
import { LessenFilter_v2 } from '../models/LessenFilter_v2';
import { AnonymousLesResult } from '../models/AnonymousLesResult';
import { Les } from '../models/Les';
import { Proefles } from '../models/Proefles';
import { ProeflesActiviteit } from '../models/ProeflesActiviteit';
import { ProefInschrijvingData } from '../models/ProefInschrijvingData';
import { ProeflesWidgetSettings } from '../models/ProeflesWidgetSettings';
import { SearchLid } from '../models/SearchLid';
import { Account } from '../models/Account';
import { CachedProfilesService } from './cached-profiles.service';

@Injectable({
  providedIn: 'root'
})

export class UserService {

  private request = inject(Request);
  private metadataService = inject(MetadataService);
  private globalService = inject(GlobalService);
  private notificationService = inject(NotificationService);
  private toastCtrl = inject(ToastController);
  private cachedProfilesService = inject(CachedProfilesService);

  private accountKeyCache = new Cache<string>("AccountKey");
  private restrictLoginCache = new Cache<string>("RestirctLogin");
  private accountCache = new Cache<Account>("Account");

  private deviceId?: DeviceId;

  public deviceInfo?: DeviceInfo;

  constructor() {
    Device.getId().then((id) => {
      this.deviceId = id;
    });
    Device.getInfo().then((info) => {
      this.deviceInfo = info;
    });
  }

  public login(username: string, password: string): Observable<boolean> {
    const userCache = new Cache<User>("User");
    const params = {
      username,
      password
    }

    const options: RequestOptions = new RequestOptions();
    options.isAnonymous = true;
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetLoginResult";
    options.params = params;

    if (!this.accountKeyCache.getItem() || !this.restrictLoginCache.getItem() || this.restrictLoginCache.getItem() === "false") {
      options.accountKey = "";
    }

    return new Observable<boolean>((observer) => {
      this.request.SendRequest(options).subscribe((user: User) => {
        if (user.ValidateUserResult === "AccountUserExist_ValidPassword") {
          userCache.setItem(user);
          this.accountKeyCache.setItem(user.Account.AccountKey);

          this.metadataService.getInitialData(username).subscribe((result) => {
            this.globalService.setLoggedIn();
            observer.next(true);
          });
        } else {
          observer.next(false);
        }
      });
    });
  }

  public loginWithToken(token: string): Observable<boolean> {
    let userCache = new Cache<User>("User");

    let options: RequestOptions = new RequestOptions();
    options.isAnonymous = true;
    options.controller = "AnonymousMasterData";
    options.endpoint = "LoginResultByOneOffToken";
    options.data = {
      token: token
    };


    return new Observable<boolean>((observer) => {
      this.request.SendRequest(options).subscribe((user: User) => {
        if (user.ValidateUserResult === "AccountUserExist_ValidPassword") {
          userCache.setItem(user);
          this.accountKeyCache.setItem(user.Account.AccountKey);

          this.metadataService.getInitialData(user.Lid.Gebruikersnaam).subscribe((result) => {
            this.globalService.setLoggedIn();
            observer.next(true);
          });
        } else {
          observer.next(false);
        }
      });
    });
  }

  public getProfileData(): Observable<ProfielData> {
    const options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "GetProfielData";
    options.params = {
      "lidId": this.globalService.getLid()!.Id
    };

    return this.request.SendRequest(options);
  }

  public getLidById(lidId: string): Observable<SearchLid> {
    let options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "GetLidById";
    options.params = {
      "lidId": lidId
    };
    return this.request.SendRequest(options);
  }

  public forgotPassword(username: string): Observable<ResetPassword> {
    let params = {
      username
    };

    let options: RequestOptions = new RequestOptions();
    options.isAnonymous = true;
    options.controller = "Account";
    options.endpoint = "ResetPassword";
    options.params = params;
    options.accountKey = options.accountKey || "AppRequest";

    if (!this.accountKeyCache.getItem() || !this.restrictLoginCache.getItem() || this.restrictLoginCache.getItem() === "false") {
      options.accountKey = "AppRequest";
    }

    return new Observable<ResetPassword>((observer) => {
      this.request.SendRequest(options).subscribe((result: ResetPassword) => {
        observer.next(result);
      });
    });
  }

  public deleteAccount(username: string): Observable<boolean> {

    let params = {
      "userName": username
    }

    let options: RequestOptions = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "RemoveUserAccount";
    options.params = params;

    return new Observable<boolean>((observer) => {
      this.request.SendRequest(options).subscribe(() => {
        observer.next(true);
      }, (error) => {
        observer.next(false);
      });
    });
  }

  public changePassword(username: string, password: string): Observable<boolean> {
    let data = {
      username: username,
      password: password
    };

    let options: RequestOptions = new RequestOptions();
    options.isAnonymous = false;
    options.controller = "Lid";
    options.endpoint = "UpdatePasswordPost";
    options.data = data;

    return new Observable<boolean>((observer) => {
      this.request.SendRequest(options).subscribe(() => {
        observer.next(true);
      }, () => {
        observer.next(false);
      });
    });
  }

  public changeUsername(lidId: string, username: string, newUsername: string): Observable<ChangeUsernameResult> {
    return new Observable<ChangeUsernameResult>((observer) => {
      this.isUsernameAvailable(lidId, newUsername).subscribe((result) => {
        if (result) {
          let params = {
            huidigeUserName: username,
            nieuweUserName: newUsername
          };

          let options: RequestOptions = new RequestOptions();
          options.controller = "Lid";
          options.endpoint = "UpdateUsername";
          options.params = params;

          this.request.SendRequest(options).subscribe((result) => {
            Authentication.UpdateUsername(newUsername);
            this.globalService.setLoggedIn();
            observer.next(ChangeUsernameResult.OK);
          },
            (error) => {
              observer.next(ChangeUsernameResult.Error)
              console.error(error);
            });
        } else {
          observer.next(ChangeUsernameResult.UsernameExists)
        }
      });
    });
  }

  public logout(removeSavedProfile = false): void {
    if (removeSavedProfile) {
      this.cachedProfilesService.removeCurrentUserProfile();
    }

    if (Capacitor.getPlatform() !== "web") {
      this.notificationService.RemoveFirebaseTokenWithToken().subscribe(() => {
        this.HandleLogout();
      });
    } else {
      this.HandleLogout();
    }
  }

  private HandleLogout(): void {
    // Logging out should be the last thing todo, because it clears the localstorage
    Authentication.Logout();
    window.location.assign('/');
  }

  public updateKortingscode(kortingscodeId: string): void {
    let options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "KortingscodeOpslaan";
    options.params = {
      "kortingscodeId": kortingscodeId
    };

    this.request.SendRequest(options).subscribe();
  }

  public getStatusLid(): Observable<string> {
    let options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "GetLidStatus";

    return this.request.SendRequest(options);
  }

  private isUsernameAvailable(lidId: string, username: string): Subject<boolean> {
    let subject = new Subject<boolean>();

    let params = {
      LidId: lidId,
      Username: username,
      IsMobile: this.request.isMobile
    };

    let options: RequestOptions = new RequestOptions();
    options.isAnonymous = true;
    options.controller = "Account";
    options.endpoint = "IsUsernameAvailable";
    options.params = params;

    this.request.SendRequest(options).subscribe((result: boolean) => {
      subject.next(result);
    });

    return subject;
  }

  public getWalletPass(): Observable<string> {
    if (!this.deviceInfo || !this.deviceId) throw new Error("deviceInfo and or deviceId not initialized");

    const options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "WalletPasOphalen";
    options.data = {
      "lidId": this.globalService.getLid()!.Id,
      "platform": this.deviceInfo.platform,
      "deviceId": this.deviceId.identifier,
      "accountKey": this.accountKeyCache.getItem(),
      "serviceUrl": Config.ServiceURL,
      "deviceModel": this.deviceInfo.model,
      "deviceOS": this.deviceInfo.osVersion,
      "deviceName": this.deviceInfo.name
    };
    //// AppDebug.Add(`Before get wallet pass. lidId: ${(options.data as any).lidId}, platform: ${(options.data as any).platform}`, true);
    return this.request.SendRequest(options, [], (error) => {
      // AppDebug.Add(`Get wallet error: ${JSON.stringify(error)}`, true);
      this.toastCtrl.create({
        message: "Fout bij ophalen wallet pas. Neem contact met de club op als dit probleem zich blijft voordoen.",
        duration: 3000,
        color: "danger"
      }).then((toast) => {
        toast.present();
      });
    });
  }

  public getExistingWalletPass(): Observable<string> {
    if (!this.deviceInfo || !this.deviceId) throw new Error("deviceInfo and or deviceId not initialized");
    
    let options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "CheckBestaandeWalletPas";
    options.data = {
      "lidId": this.globalService.getLid()!.Id,
      "platform": this.deviceInfo.platform,
      "deviceId": this.deviceId.identifier,
      "accountKey": this.accountKeyCache.getItem(),
      "serviceUrl": Config.ServiceURL,
      "deviceModel": this.deviceInfo.model,
      "deviceOS": this.deviceInfo.osVersion,
      "deviceName": this.deviceInfo.name
    };
    //// AppDebug.Add(`Before get wallet pass. lidId: ${(options.data as any).lidId}, platform: ${(options.data as any).platform}`, true);
    return this.request.SendRequest(options, [], (error) => {
      // AppDebug.Add(`Get wallet error: ${JSON.stringify(error)}`, true);
      this.toastCtrl.create({
        message: "Fout bij ophalen wallet pas. Neem contact met de club op als dit probleem zich blijft voordoen.",
        duration: 3000,
        color: "danger"
      }).then((toast) => {
        toast.present();
      });
    });
  }

  public resetWalletPas(): Observable<boolean> {
    let options = new RequestOptions();
    options.controller = "Lid";
    options.endpoint = "ResetWalletPas";
    options.data = {
      "lidId": this.globalService.getLid()!.Id
    };

    return this.request.SendRequest(options);
  }

  public getInitialDataForWidgets(): Observable<InitialDataLoad> {
    const options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetInitialDataForWidgets";
    options.params = {
      userName: null,
      screenWidth: 800,
      disableThemeCss: false
    };
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;

    return this.request.SendRequest(options).pipe(
      tap((result) => {
        const account = result.account;
        this.accountCache.setItem(account);
      })
    );
  }

  public getWidget(): Observable<Widget> {
    let options = new RequestOptions();
    options.controller = "Account";
    options.endpoint = "GetWidget";
    options.params = {
      widgetId: this.globalService.widgetId
    };
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;

    return this.request.SendRequest(options);
  }

  public getProeflesSettings(): Observable<ProeflesWidgetSettings> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetProeflesSettings";
    options.params = {
      widgetId: this.globalService.widgetId
    };
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;

    return this.request.SendRequest(options);
  }

  public getLanden(): Observable<Land[]> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetLanden";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;

    return this.request.SendRequest(options);
  }

  public getVestigingen(): Observable<Vestiging[]> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetVestigingen";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;

    return this.request.SendRequest(options);
  }

  public getProeflessen(abonnementsvormId?: string | null, vestigingId?: string | null, activiteitId?: string | null): Observable<Proefles[]> {
    const options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetProeflessen";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = {
      AbonnementsvormId: abonnementsvormId,
      VestigingId: vestigingId,
      ActiviteitId: activiteitId
    };

    return this.request.SendRequest(options);
  }

  public getProeflesActiviteiten(abonnementsvormId?: string | null, vestigingId?: string | null): Observable<ProeflesActiviteit[]> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetProeflesActiviteiten";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = {
      AbonnementsvormId: abonnementsvormId,
      VestigingId: vestigingId
    };

    return this.request.SendRequest(options);
  }

  /**
   * Proefles inschrijving opslaan.
   * @param data Gegevens van de les en het lid.
   * @returns Een observable met de MSP URL.
   */
  public proefInschrijvingOpslaan(data: ProefInschrijvingData): Observable<string> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "ProefInschrijvingOpslaan";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = data;
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options);
  }

  public proefInschrijvingOpslaanBestaandLid(oneOffToken: string, data: ProefInschrijvingData): Observable<string> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "ProefInschrijvingOpslaanBestaandLid";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = {data: data, oneOffToken: oneOffToken};
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options);
  }

  public getLesRoosterFilter(): Observable<LesRoosterFilter> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetLesRoosterFilter";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = {};
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options);
  }

  public getLesRooster(filter: LessenFilter_v2): Observable<LesRooster> {
    const options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetLesRooster_v2";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = filter;
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options);
  }

  public getLesRoosterTopDrie(filter: LessenFilter): Observable<LesRooster> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetLesRoosterTopDrie";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = filter;
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options);
  }

  public getLes(lesId: string): Observable<Les> {
    let options = new RequestOptions();
    options.controller = "LesReserveren";
    options.endpoint = "GetLes";
    options.accountKey = Authentication.GetAccountKey();
    options.params = {
      les: lesId
    };

    return this.request.SendRequest(options);
  }

  public getActiviteitInfo(activiteitInfoId: string, medewerkerId: string): Observable<AnonymousLesResult> {
    let options = new RequestOptions();
    options.controller = "AnonymousMasterData";
    options.endpoint = "GetAnonymousLesResult";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.params = {
      activiteitInfoId: activiteitInfoId,
      medewerkerId: medewerkerId
    };

    return this.request.SendRequest(options);
  }

  public getIsLesReserveerbaar(lesId: string, lidId?: string): Observable<boolean> {
    let options = new RequestOptions();
    options.controller = "LesReserveren";
    options.endpoint = "IsLesReserveerbaar";
    options.accountKey = Authentication.GetAccountKey();
    options.params = {
      lesId: lesId,
      lidId: lidId
    };

    return this.request.SendRequest(options);
  }

  public aanmeldenLes(datum: Date, isGastaanmelding: boolean, isVasteDeelname: boolean, lesId: string, username: string): Observable<void> {
    let options = new RequestOptions();
    options.controller = "LesReserveren";
    options.endpoint = "AanmeldenLes";
    options.accountKey = Authentication.GetAccountKey();
    options.data = {
      datum: datum,
      isgastaanmelding: isGastaanmelding,
      isvastedeelname: isVasteDeelname,
      les: lesId,
      username: username
    };

    return this.request.SendRequest(options);
  }

  public afmeldenLes(abonnementId: string, lesId: string, status: string, vasteDeelnameVerwijderen: boolean): Observable<void> {
    let options = new RequestOptions();
    options.controller = "LesReserveren";
    options.endpoint = "AfmeldenLes";
    options.accountKey = Authentication.GetAccountKey();
    options.data = {
      abonnement: abonnementId,
      les: lesId,
      status: status,
      vastedeelnameverwijderen: vasteDeelnameVerwijderen
    };

    return this.request.SendRequest(options);
  }

  public getAdresGegevens(postcode: string, huisnummer: string, toevoeging: string): Observable<AddressInfo> {
    let options = new RequestOptions();
    options.controller = "Account";
    options.endpoint = "OphalenAdresgegevens";
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.data = {
      "postcode": postcode,
      "huisnummer": huisnummer,
      "toevoeging": toevoeging || ""
    };

    return this.request.SendRequest(options);
  }


  public saveUser(lidgegevens: Lid): Observable<string> {
    let options = new RequestOptions();
    options.controller = "Account";
    options.endpoint = "SaveUser";
    options.data = lidgegevens;
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options, null, (err) => { console.error(err); debugger; });
  }

  public updateUser(oneOffToken: string, lidgegevens: Lid): Observable<string> {
    let options = new RequestOptions();
    options.controller = "Account";
    options.endpoint = "UpdateUserByOneOffToken";
    options.data = {lid: lidgegevens, oneOffToken: oneOffToken};
    options.accountKey = Authentication.GetAccountKey();
    options.isAnonymous = true;
    options.widgetId = this.globalService.widgetId;

    return this.request.SendRequest(options, null, (err) => { console.error(err); debugger; });
  }
}

export enum ChangeUsernameResult {
  OK,
  UsernameExists,
  Error
}
